import React from 'react';
import { View, Text, Pressable, ScrollView, ViewStyle, TextStyle, Platform } from 'react-native';
import { useTheme } from '../context/themeContext';
import { ColorScheme } from '../theme/colors';
import Icon from './icon';


// Overlay drawers/modals must anchor to the VIEWPORT, not the nearest
// scrollable content box. On web the shell's content area is `overflow:auto`
// (a scroll container), so `position:absolute` would size to that short box —
// use `fixed` on web so the overlay is always full viewport height.
export const OVERLAY_POSITION = (Platform.OS === 'web' ? 'fixed' : 'absolute') as 'absolute';

// SK Voice primitives — recreated EXACTLY from the HTML style-builders
// (card/btn/chip/switch/pill/dot). Monochrome; sizes/radii/weights verbatim.

export function useC() { return useTheme().theme; }

// ── Card (bg card, 1px line, radius 10, shadow-sm) ──
export function Card({ children, style }: { children?: React.ReactNode; style?: ViewStyle }) {
  const t = useTheme().theme; const c = t.colors;
  return (
    <View style={[{ backgroundColor: c.card, borderWidth: 1, borderColor: c.line, borderRadius: 10, ...(t.shadows.sm as object) }, style]}>
      {children}
    </View>
  );
}

// ── KPI card: label (11/500 ink3) · value (26/600 -.02em) · sub (11 ink4) ──
export function KpiCard({ label, value, sub, valueSize = 26, labelUpper = false }:
  { label: string; value: string; sub?: string; valueSize?: number; labelUpper?: boolean }) {
  const t = useTheme().theme; const c = t.colors;
  return (
    <Card style={{ padding: 15, paddingVertical: 14 }}>
      <Text style={{ fontSize: labelUpper ? 10.5 : 11, color: c.ink3, fontWeight: '600', marginBottom: 8, fontFamily: t.font.sans, letterSpacing: labelUpper ? 0.6 : 0, textTransform: labelUpper ? 'uppercase' : 'none' }}>{label}</Text>
      <Text style={{ fontSize: valueSize, fontWeight: '600', letterSpacing: -0.5, lineHeight: valueSize, color: c.ink, fontFamily: t.font.sans }}>{value}</Text>
      {sub != null && <Text style={{ fontSize: 11, color: c.ink4, marginTop: 6, fontFamily: t.font.sans }}>{sub}</Text>}
    </Card>
  );
}

// ── Chip: solid | outline | mono | default ──
export type ChipKind = 'solid' | 'outline' | 'mono' | 'default';
export function Chip({ kind = 'default', children }: { kind?: ChipKind; children: React.ReactNode }) {
  const c = useTheme().theme.colors; const t = useTheme().theme;
  const base: ViewStyle = { flexDirection: 'row', alignItems: 'center', gap: 5, height: 22, paddingHorizontal: 9, borderRadius: 20, alignSelf: 'flex-start' };
  const styles: Record<ChipKind, { v: ViewStyle; txt: TextStyle }> = {
    solid:   { v: { backgroundColor: c.ink }, txt: { color: c.paper } },
    outline: { v: { borderWidth: 1, borderColor: c.lineStrong }, txt: { color: c.ink2 } },
    mono:    { v: { backgroundColor: c.paper2, borderWidth: 1, borderColor: c.line }, txt: { color: c.ink3, fontFamily: t.font.mono } },
    default: { v: { backgroundColor: c.paper2, borderWidth: 1, borderColor: c.line }, txt: { color: c.ink2 } },
  };
  return (
    <View style={[base, styles[kind].v]}>
      <Text style={{ fontSize: 11, fontWeight: '500', fontFamily: t.font.sans, ...styles[kind].txt }}>{children}</Text>
    </View>
  );
}

// ── Button: primary | default | ghost | danger ──
export type BtnKind = 'primary' | 'default' | 'ghost' | 'danger';
export function Button({ kind = 'default', label, icon, onPress, disabled, style }:
  { kind?: BtnKind; label: string; icon?: string; onPress?: () => void; disabled?: boolean; style?: ViewStyle }) {
  const c = useTheme().theme.colors; const t = useTheme().theme;
  const map: Record<BtnKind, { v: ViewStyle; fg: string }> = {
    primary: { v: { backgroundColor: c.ink }, fg: c.paper },
    default: { v: { backgroundColor: c.card, borderWidth: 1, borderColor: c.line }, fg: c.ink },
    ghost:   { v: { backgroundColor: 'transparent' }, fg: c.ink2 },
    danger:  { v: { backgroundColor: 'transparent', borderWidth: 1, borderColor: c.lineStrong }, fg: c.ink },
  };
  const s = map[kind];
  return (
    <Pressable onPress={onPress} disabled={disabled}
      style={[{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, height: 32, paddingHorizontal: 13, borderRadius: 7, opacity: disabled ? 0.4 : 1 }, s.v, style]}>
      {icon ? <Icon name={icon} size={14} color={s.fg} /> : null}
      <Text style={{ fontSize: 12.5, fontWeight: '500', color: s.fg, fontFamily: t.font.sans }}>{label}</Text>
    </Pressable>
  );
}

// ── Switch (track + knob). sm = 28×16/12; else 34×20/16. ──
export function Switch({ on, sm, onPress }: { on: boolean; sm?: boolean; onPress?: () => void }) {
  const c = useTheme().theme.colors;
  const w = sm ? 28 : 34, h = sm ? 16 : 20, d = sm ? 12 : 16, tx = sm ? 10 : 14;
  return (
    <Pressable onPress={onPress}
      style={{ width: w, height: h, borderRadius: 20, borderWidth: 1, borderColor: on ? c.ink : c.lineStrong, backgroundColor: on ? c.ink : c.paper3, justifyContent: 'center' }}>
      <View style={{ position: 'absolute', top: 1, left: 1, width: d, height: d, borderRadius: 999, backgroundColor: on ? c.paper : c.card, transform: [{ translateX: on ? tx : 0 }] }} />
    </Pressable>
  );
}

// ── Outcome color (completed = green · missed = amber · dropped = red) ──
// Single source for the outcome hue so every dot/pill site stays in sync.
export function outcomeColor(outcome: string, c: ColorScheme): string {
  if (outcome === 'completed') return c.outcomeOk;
  if (outcome === 'missed') return c.outcomeMiss;
  return c.outcomeDrop;
}

// ── Outcome dot — solid colored fill by outcome ──
export function OutcomeDot({ outcome }: { outcome: string }) {
  const c = useTheme().theme.colors;
  return <View style={{ width: 7, height: 7, borderRadius: 999, backgroundColor: outcomeColor(outcome, c) }} />;
}

// ── Pill (bordered inline label, e.g. direction/outcome) ──
export function Pill({ children, filled }: { children: React.ReactNode; filled?: boolean }) {
  const c = useTheme().theme.colors; const t = useTheme().theme;
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, height: 24, paddingHorizontal: 11, borderWidth: 1, borderColor: c.line, borderRadius: 20, backgroundColor: filled ? c.paper2 : 'transparent', alignSelf: 'flex-start' }}>
      {typeof children === 'string'
        ? <Text style={{ fontSize: 11.5, color: c.ink2, fontFamily: t.font.sans }}>{children}</Text>
        : children}
    </View>
  );
}

// ── Section label (uppercase 10.5/600 ink4) ──
export function SectionLabel({ children, style }: { children: React.ReactNode; style?: TextStyle }) {
  const c = useTheme().theme.colors; const t = useTheme().theme;
  return <Text style={[{ fontSize: 10.5, fontWeight: '600', letterSpacing: 0.6, textTransform: 'uppercase', color: c.ink4, fontFamily: t.font.sans }, style]}>{children}</Text>;
}

// ── Drawer (right-side slide-in panel over a scrim) ──
export function Drawer({ open, onClose, width = 560, children }:
  { open: boolean; onClose?: () => void; width?: number; children?: React.ReactNode }) {
  const t = useTheme().theme; const c = t.colors;
  if (!open) return null;
  return (
    <View style={{ position: OVERLAY_POSITION, top: 0, left: 0, right: 0, bottom: 0, flexDirection: 'row', justifyContent: 'flex-end' }}>
      {/* Scrim — sibling behind the panel; press closes */}
      <Pressable onPress={onClose} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.32)' }} />
      {/* Panel — plain View so presses don't reach the scrim */}
      <View style={{ width, maxWidth: '94%' as any, height: '100%', backgroundColor: c.card, borderLeftWidth: 1, borderLeftColor: c.line, flexDirection: 'column', ...(t.shadows.lg as object) }}>
        {children}
      </View>
    </View>
  );
}

// ── Modal (centered card over a scrim) ──
export function Modal({ open, onClose, width = 440, children }:
  { open: boolean; onClose?: () => void; width?: number; children?: React.ReactNode }) {
  const t = useTheme().theme; const c = t.colors;
  if (!open) return null;
  return (
    <View style={{ position: OVERLAY_POSITION, top: 0, left: 0, right: 0, bottom: 0, alignItems: 'center', justifyContent: 'center' }}>
      <Pressable onPress={onClose} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.32)' }} />
      <View style={{ width, maxWidth: '92%' as any, maxHeight: '88%' as any, backgroundColor: c.card, borderRadius: 14, borderWidth: 1, borderColor: c.line, overflow: 'hidden', ...(t.shadows.lg as object) }}>
        <ScrollView contentContainerStyle={{ padding: 22 }} showsVerticalScrollIndicator={false}>
          {children}
        </ScrollView>
      </View>
    </View>
  );
}

// ── Screen container (centered column, max-width, top padding) ──
export function ScreenBody({ maxWidth = 1120, children }: { maxWidth?: number; children: React.ReactNode }) {
  return (
    <View style={{ width: '100%', alignItems: 'center' }}>
      <View style={{ width: '100%', maxWidth, paddingHorizontal: 28, paddingTop: 24, paddingBottom: 80 }}>
        {children}
      </View>
    </View>
  );
}
